<!-- src/components/ContactLogModal.vue -->
<!-- 신규회원 관리의 [연락 기록] 모달: 최근 연락 3건을 보고 새 연락 기록을 남긴다 -->
<!-- (docs/architecture/admin-member-management-design.md 5절) -->
<template>
  <div
    class="cl-overlay"
    @mousedown.self="onOverlayMousedown"
    @click.self="onOverlayClick"
  >
    <div
      ref="dialog"
      class="cl-modal"
      role="dialog"
      aria-modal="true"
      aria-labelledby="contact-log-title"
      tabindex="-1"
    >
      <div class="cl-head">
        <div class="cl-head-text">
          <h3 id="contact-log-title" class="cl-title">
            {{ member.name || "회원" }}
            <span class="cl-title-sub">연락 기록</span>
          </h3>
          <a
            v-if="phoneDigits"
            class="cl-phone"
            :href="`tel:${phoneDigits}`"
            title="전화 걸기"
            @click="onPhoneClick"
          >
            {{ prettyPhone(member.phone_number) }}
          </a>
        </div>
        <button
          type="button"
          class="cl-x"
          aria-label="닫기"
          :disabled="saving"
          @click="requestClose"
        >
          ×
        </button>
      </div>

      <!-- 최근 연락 기록 3건. 모달이 화면보다 높으면 이 칸만 줄어들고 안에서 스크롤된다(결과·내용·[저장]은 늘 보임) -->
      <section class="cl-recent">
        <h4 class="cl-section-title">최근 연락</h4>
        <p v-if="loadingNotes" class="cl-muted">불러오는 중…</p>
        <p v-else-if="notesError" class="cl-muted">
          연락 기록을 불러오지 못했습니다.
          <button type="button" class="cl-link" @click="loadNotes">
            다시 시도
          </button>
        </p>
        <p v-else-if="recentContacts.length === 0" class="cl-muted">
          아직 연락 기록이 없습니다.
        </p>
        <ul v-else class="cl-list">
          <li v-for="n in recentContacts" :key="n.id" class="cl-item">
            <div class="cl-item-meta">
              <span
                class="cl-badge"
                :class="`tone-${contactResultTone(n.contact_result)}`"
              >
                {{ contactResultLabel(n.contact_result) || "연락" }}
              </span>
              <span class="cl-item-date">{{ noteDateTime(n.created_at) }}</span>
              <span class="cl-item-author">{{ n.author_name || "—" }}</span>
            </div>
            <!-- 두 줄까지만(전체는 마우스를 올리면, 회원 상세의 기록에도 있음). 긴 기록이 결과·[저장]을 화면 밖으로 밀었다 -->
            <p v-if="n.content" class="cl-item-content" :title="n.content">
              {{ n.content }}
            </p>
          </li>
        </ul>
      </section>

      <!-- 새 연락 기록 -->
      <form class="cl-form" novalidate @submit.prevent="save">
        <fieldset class="cl-results" :disabled="saving">
          <legend class="cl-section-title">
            결과 <span class="cl-required">필수</span>
          </legend>
          <div class="cl-radios">
            <label
              v-for="r in contactResults"
              :key="r.value"
              class="cl-radio"
              :class="[
                `tone-${contactResultTone(r.value)}`,
                { checked: result === r.value },
              ]"
            >
              <input
                ref="resultInputs"
                v-model="result"
                type="radio"
                name="contact-log-result"
                :value="r.value"
                @change="resultError = false"
              />
              <span>{{ r.label }}</span>
            </label>
          </div>
          <p v-if="resultError" class="cl-error" role="alert">
            통화 · 부재 · 문자 중 하나를 골라 주세요.
          </p>
        </fieldset>

        <div class="cl-field">
          <label class="cl-section-title" for="contact-log-content">
            내용 <span class="cl-optional">선택</span>
          </label>
          <textarea
            id="contact-log-content"
            v-model="content"
            class="cl-textarea"
            :maxlength="maxContent"
            rows="3"
            placeholder="통화 내용, 다음 연락 계획 등"
            :disabled="saving"
          ></textarea>
          <div class="cl-counter">{{ content.length }}/{{ maxContent }}</div>
        </div>

        <p v-if="saveError" class="cl-error" role="alert">{{ saveError }}</p>

        <div class="cl-actions">
          <button
            type="button"
            class="cl-btn"
            :disabled="saving"
            @click="requestClose"
          >
            취소
          </button>
          <button
            type="submit"
            class="cl-btn cl-btn-primary"
            :disabled="saving"
          >
            {{ saving ? "저장 중..." : "저장" }}
          </button>
        </div>
      </form>
    </div>
  </div>
</template>

<script>
import {
  CONTACT_RESULTS,
  contactResultLabel,
  contactResultTone,
  confirmDiscardDraft,
  createNote,
  fetchNotes,
  noteDateTime,
  prettyPhone,
} from "@/lib/memberNotes";
import { errorState, hideError } from "@/plugins/errorHandler";

// 모달에 보여 주는 최근 연락 기록 수
const RECENT_LIMIT = 3;

export default {
  name: "ContactLogModal",
  props: {
    // 신규회원 목록의 한 줄: { user_id, name, phone_number, ... }
    member: { type: Object, required: true },
  },
  emits: ["close", "saved"],
  data() {
    return {
      contactResults: CONTACT_RESULTS,
      maxContent: 1000,
      notes: [],
      loadingNotes: false,
      notesError: false,
      result: "",
      content: "",
      resultError: false,
      saveError: "",
      saving: false,
      pressedOnOverlay: false,
      openedAt: 0,
      returnFocusTo: null,
      focusBeforePopup: null, // 전역 오류 팝업이 뜨기 전 모달 안의 초점(팝업이 닫히면 돌려준다)
      dialedAt: 0, // 전화번호(tel:)를 누른 때. 그 직후의 beforeunload는 앱을 떠나는 것이 아니다
    };
  },
  computed: {
    // 서버는 최신 순으로 준다. 메모는 빼고 연락만 3건
    recentContacts() {
      return this.notes
        .filter((n) => n.kind === "contact")
        .slice(0, RECENT_LIMIT);
    },
    phoneDigits() {
      return String(this.member.phone_number || "").replace(/\D/g, "");
    },
    // 저장 실패 때 apiJson이 띄우는 전역 오류 팝업(ErrorPopup, 모달 위)
    errorPopupShown() {
      return errorState.show;
    },
  },
  watch: {
    // 오류 팝업이 뜨면 초점을 팝업의 [확인]으로 옮기고, 닫히면 모달로 돌려준다(6차 리뷰: 초점이 팝업 아래 모달에 갇혀 [확인]에 갈 수
    // 없었고, Enter는 [저장]을 다시 눌렀다)
    errorPopupShown(shown) {
      if (shown) {
        this.focusBeforePopup = document.activeElement;
        this.$nextTick(() => this.focusErrorPopup());
        return;
      }
      const el = this.focusBeforePopup;
      this.focusBeforePopup = null;
      this.$nextTick(() => {
        const dialog = this.$refs.dialog;
        if (!dialog) return;
        if (el && el !== dialog && dialog.contains(el) && !el.disabled) {
          el.focus();
        } else {
          dialog.focus();
        }
      });
    },
  },
  created() {
    this.loadNotes();
  },
  mounted() {
    this.openedAt = Date.now();
    // 모달을 연 버튼으로 닫은 뒤 초점을 돌려준다
    this.returnFocusTo = document.activeElement;
    document.addEventListener("keydown", this.onKeydown);
    this.$nextTick(() => this.$refs.dialog?.focus());
  },
  beforeUnmount() {
    document.removeEventListener("keydown", this.onKeydown);
    const el = this.returnFocusTo;
    if (el && typeof el.focus === "function" && document.contains(el)) {
      el.focus();
    }
  },
  methods: {
    prettyPhone,
    contactResultLabel,
    contactResultTone,
    noteDateTime,

    async loadNotes() {
      this.loadingNotes = true;
      this.notesError = false;
      try {
        const notes = await fetchNotes(this.member.user_id);
        this.notes = Array.isArray(notes) ? notes : [];
      } catch (e) {
        console.error("연락 기록 불러오기 실패:", e);
        this.notesError = true;
      } finally {
        this.loadingNotes = false;
      }
    },

    async save() {
      if (this.saving) return;
      this.saveError = "";
      if (!this.result) {
        this.resultError = true;
        this.$refs.resultInputs?.[0]?.focus();
        return;
      }

      this.saving = true;
      try {
        const note = await createNote(this.member.user_id, {
          kind: "contact",
          contact_result: this.result,
          content: this.content.trim(),
        });
        this.$emit("saved", note);
      } catch (e) {
        console.error("연락 기록 저장 실패:", e);
        this.saveError = "저장하지 못했습니다. 잠시 후 다시 시도해 주세요.";
      } finally {
        this.saving = false;
      }
    },

    // 쓰던 내용이 있는지 (다른 화면으로 나갈 때 NewMembersView도 묻는다). 고른 결과만 있어도 쓰던 기록이다: 부재는 보통 내용 없이
    // 남기는데, 결과만 고른 모달이 Esc·바깥 누름에 묻지 않고 닫혔다(4차 리뷰)
    hasDraft() {
      return !!this.result || !!this.content.trim();
    },
    confirmDiscard() {
      return confirmDiscardDraft();
    },
    // 전화 걸기(tel:)도 브라우저에는 페이지 이동이라 beforeunload가 불린다. 그 순간에는 쓰던 글을 묻지 않는다(5차 리뷰: 부재를 고르고
    // 다시 걸려고 번호를 누르면 "사이트에서 나가시겠습니까?"가 떠, [취소]면 전화가 걸리지 않았다). NewMembersView.onBeforeUnload가 본다
    onPhoneClick() {
      this.dialedAt = Date.now();
    },
    isDialing() {
      return Date.now() - this.dialedAt < 1000;
    },

    // 저장 중에는 닫지 않는다. 쓰던 내용이 있으면 한 번 묻는다
    requestClose() {
      if (this.saving) return;
      if (this.hasDraft() && !this.confirmDiscard()) return;
      this.$emit("close");
    },

    // 모달을 연 버튼의 두 번째 클릭(더블클릭)은 바깥 누름이 아니다. 넓은 화면에서는 [연락 기록] 버튼이 가운데 모달 밖이라 두 번째
    // 클릭이 바로 모달을 닫았다(4차 리뷰). 연 뒤 0.5초 안의 누름과 연속 클릭(detail > 1)은 무시한다
    onOverlayMousedown(event) {
      if (event.detail > 1 || Date.now() - this.openedAt < 500) return;
      this.pressedOnOverlay = true;
    },
    // 모달 안에서 끌어 고르다 바깥에서 놓은 경우는 닫지 않는다
    onOverlayClick() {
      const pressed = this.pressedOnOverlay;
      this.pressedOnOverlay = false;
      if (pressed) this.requestClose();
    },

    focusErrorPopup() {
      document.querySelector(".error-popup .error-button")?.focus();
    },

    onKeydown(e) {
      // 전역 오류 팝업이 떠 있으면 키는 팝업 것이다: Esc는 팝업만 닫고(쓰던 연락은 그대로, 모달을 닫을지 묻지 않음), Tab은 팝업의
      // [확인]에 머문다. Enter는 [확인]이 받는다
      if (errorState.show) {
        if (e.key === "Escape" || e.key === "Esc") {
          e.preventDefault();
          hideError();
        } else if (e.key === "Tab") {
          e.preventDefault();
          this.focusErrorPopup();
        }
        return;
      }
      if (e.key === "Escape" || e.key === "Esc") {
        e.preventDefault();
        this.requestClose();
        return;
      }
      if (e.key === "Tab") this.trapTab(e);
    },

    // Tab·Shift+Tab이 모달 밖(뒤 화면의 메뉴·줄)으로 나가지 않게 처음과 끝에서 돌린다.
    // 예전에는 뒤 화면으로 나간 초점에서 Enter를 누르면 쓰던 내용을 묻지 않고 다른 화면으로 갔다
    trapTab(e) {
      const dialog = this.$refs.dialog;
      if (!dialog) return;
      // :disabled는 저장 중 꺼지는 fieldset 안 라디오까지 뺀다
      const items = [
        ...dialog.querySelectorAll(
          'a[href], button:not(:disabled), textarea:not(:disabled), input:not(:disabled), select:not(:disabled), [tabindex]:not([tabindex="-1"])'
        ),
      ].filter((el) => el.getClientRects().length > 0);
      const active = document.activeElement;
      if (items.length === 0) {
        e.preventDefault();
        dialog.focus();
        return;
      }
      const first = items[0];
      const last = items[items.length - 1];
      const inside = dialog.contains(active) && active !== dialog;
      if (e.shiftKey && (!inside || active === first)) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && (!inside || active === last)) {
        e.preventDefault();
        first.focus();
      }
    },
  },
};
</script>

<style scoped>
.cl-overlay {
  position: fixed;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
  background: rgba(0, 0, 0, 0.7);
  z-index: 1000;
}

/* 세로 flex: 화면이 낮으면(1366x768 노트북의 실제 창 약 625~657px) 최근 연락 칸만 줄고 안에서 스크롤된다. 예전에는 모달 전체만
   스크롤되어 긴 연락 기록 세 건이면 [취소][저장]이 화면 아래에 숨었다(6차 리뷰). 그래도 넘치면 모달 전체가 스크롤된다 */
.cl-modal {
  display: flex;
  flex-direction: column;
  width: 480px;
  max-width: 100%;
  max-height: calc(100vh - 32px);
  overflow-y: auto;
  background: #1b1d1e;
  border: 1px solid #3a3c3d;
  border-radius: 10px;
  padding: 20px;
  color: #fff;
  outline: none;
}

/* 머리: 이름 + 전화번호 + 닫기 */
.cl-head {
  flex-shrink: 0;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 16px;
}

.cl-head-text {
  min-width: 0;
}

.cl-title {
  margin: 0;
  font-size: 20px;
  font-weight: 700;
  line-height: 1.3;
  word-break: keep-all;
}

.cl-title-sub {
  margin-left: 6px;
  font-size: 15px;
  font-weight: 400;
  color: #8e8e8e;
}

.cl-phone {
  display: inline-block;
  margin-top: 4px;
  font-size: 14px;
  color: #9d9d9d;
  text-decoration: none;
}

.cl-phone:hover {
  color: #78ff47;
}

.cl-x {
  flex-shrink: 0;
  width: 32px;
  height: 32px;
  border-radius: 6px;
  border: 1px solid #333;
  background: transparent;
  color: #c8c8c8;
  font-size: 20px;
  line-height: 1;
  cursor: pointer;
}

.cl-x:hover:not(:disabled) {
  background: #252728;
  color: #fff;
}

.cl-section-title {
  display: block;
  margin: 0 0 8px;
  padding: 0;
  font-size: 14px;
  font-weight: 600;
  color: #c8c8c8;
}

.cl-required {
  margin-left: 4px;
  font-size: 12px;
  font-weight: 400;
  color: #ef4444;
}

.cl-optional {
  margin-left: 4px;
  font-size: 12px;
  font-weight: 400;
  color: #8e8e8e;
}

/* 최근 연락: 남는 높이만 쓰고 넘치면 안에서 스크롤. 기록이 있으면 제목과 첫 기록 일부는 늘 보이게 112px까지만 준다 */
.cl-recent {
  flex: 0 1 auto;
  overflow-y: auto;
  padding-bottom: 16px;
  margin-bottom: 16px;
  border-bottom: 1px solid #333;
}

.cl-recent:has(.cl-list) {
  min-height: 112px;
}

.cl-muted {
  margin: 0;
  font-size: 14px;
  color: #8e8e8e;
}

.cl-link {
  margin-left: 4px;
  padding: 0;
  border: 0;
  background: none;
  color: #78ff47;
  font-size: 14px;
  text-decoration: underline;
  cursor: pointer;
}

.cl-list {
  list-style: none;
  margin: 0;
  padding: 0;
  display: grid;
  gap: 8px;
}

.cl-item {
  padding: 10px 12px;
  border-radius: 8px;
  background: #0a0a0a;
  border: 1px solid #262626;
}

.cl-item-meta {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  font-size: 13px;
  color: #9d9d9d;
}

.cl-item-author {
  color: #c8c8c8;
}

.cl-item-content {
  margin: 6px 0 0;
  font-size: 14px;
  line-height: 1.5;
  color: #e5e5e5;
  white-space: pre-wrap;
  word-break: keep-all;
  overflow-wrap: anywhere;
  /* 최근 연락은 두 줄까지(…). 내용은 1000자까지라 768px 높이 노트북에서 결과·[저장]이 모달 아래로 밀렸다(2차 리뷰) */
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  overflow: hidden;
}

/* 배지 (결과 색: 통화·문자 초록, 부재 노랑) */
.cl-badge {
  display: inline-flex;
  align-items: center;
  height: 22px;
  padding: 0 8px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 600;
  white-space: nowrap;
  border: 1px solid rgba(255, 255, 255, 0.15);
  background: rgba(255, 255, 255, 0.06);
  color: #c8c8c8;
}

.cl-badge.tone-safe {
  color: #22c55e;
  background: rgba(34, 197, 94, 0.12);
  border-color: rgba(34, 197, 94, 0.35);
}

.cl-badge.tone-warning {
  color: #fbbf24;
  background: rgba(251, 191, 36, 0.12);
  border-color: rgba(251, 191, 36, 0.35);
}

/* 입력 */
.cl-form {
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.cl-results {
  margin: 0;
  padding: 0;
  border: 0;
  min-width: 0;
}

.cl-radios {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 8px;
}

.cl-radio {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 44px;
  border-radius: 8px;
  border: 1px solid #333;
  background: #151718;
  color: #c8c8c8;
  font-size: 15px;
  cursor: pointer;
  transition: background 0.15s, border-color 0.15s, color 0.15s;
}

.cl-radio input {
  position: absolute;
  opacity: 0;
  width: 1px;
  height: 1px;
  pointer-events: none;
}

.cl-radio:hover {
  background: #252728;
}

/* 초점 표시는 키보드로 옮겼을 때만(:focus-visible), 고른 상태(초록·노랑 테두리)와 헷갈리지 않게 흰색.
   예전 :focus-within은 마우스로 고르거나 결과 없이 [저장]을 눌러 첫 칸(통화)으로 초점이 갈 때도 초록 테두리를 그려
   통화가 골라진 것처럼 보였다 */
.cl-radio:has(input:focus-visible) {
  outline: 2px solid rgba(255, 255, 255, 0.85);
  outline-offset: 2px;
}

.cl-radio.checked.tone-safe {
  color: #22c55e;
  background: rgba(34, 197, 94, 0.14);
  border-color: #22c55e;
  font-weight: 600;
}

.cl-radio.checked.tone-warning {
  color: #fbbf24;
  background: rgba(251, 191, 36, 0.14);
  border-color: #fbbf24;
  font-weight: 600;
}

.cl-results:disabled .cl-radio {
  opacity: 0.6;
  cursor: not-allowed;
}

.cl-field {
  display: flex;
  flex-direction: column;
}

.cl-textarea {
  width: 100%;
  min-height: 84px;
  padding: 10px 12px;
  border: 1px solid #333;
  border-radius: 8px;
  background: #151718;
  color: #fff;
  font-size: 14px;
  font-family: inherit;
  line-height: 1.5;
  resize: vertical;
  /* 쓰는 중에도 읽을 때(.cl-item-content)처럼 한국어 낱말 중간에서 줄을 바꾸지 않는다(3차 리뷰) */
  word-break: keep-all;
  overflow-wrap: anywhere;
}

.cl-textarea:focus {
  outline: none;
  border-color: #78ff47;
  box-shadow: 0 0 0 2px rgba(120, 255, 71, 0.2);
}

.cl-counter {
  margin-top: 4px;
  text-align: right;
  font-size: 12px;
  color: #6b7280;
}

.cl-error {
  margin: 8px 0 0;
  font-size: 13px;
  color: #ef4444;
}

.cl-form > .cl-error {
  margin: 0;
}

.cl-actions {
  display: flex;
  gap: 12px;
}

.cl-btn {
  flex: 1;
  height: 42px;
  padding: 0 12px;
  border-radius: 6px;
  border: 1px solid #333;
  background: #151718;
  color: #fff;
  font-size: 15px;
  cursor: pointer;
  transition: background 0.2s;
}

.cl-btn:hover:not(:disabled) {
  background: #252728;
}

.cl-btn-primary {
  border-color: #78ff47;
  background: #78ff47;
  color: #000;
  font-weight: 600;
}

.cl-btn-primary:hover:not(:disabled) {
  background: #6bff3a;
  border-color: #6bff3a;
}

.cl-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

@media (max-width: 480px) {
  .cl-modal {
    padding: 16px;
  }

  .cl-title {
    font-size: 18px;
  }
}
</style>
